import { ReactElement } from "react";
import { Theme } from "@mui/material";
import { numberWithCommas, numericFontSize } from "../../utils";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

interface WidgetProps {
  title: string;
  total: number;
  icon: ReactElement;
  type?: "white" | "primary" | "link" | "error" | "warning";
  unit?: string;
  /** When set, renders "{total}/{denominator}" (e.g. branches submitted/total). */
  denominator?: number;
  onClick?: () => void;
}

export default function Widget({
  title,
  total,
  icon,
  type = "primary",
  unit = "รายการ",
  denominator,
  onClick,
}: WidgetProps) {
  const color = (theme: Theme) => {
    if (type === "white") {
      return "#fff";
    } else if (type === "link") {
      return theme.palette.link.main;
    } else if (type === "error") {
      return theme.palette.error.main;
    } else if (type === "warning") {
      return theme.palette.warning.main;
    } else {
      return theme.palette.primary.main;
    }
  };

  return (
    <Paper
      onClick={onClick}
      sx={{
        height: 100,
        bgcolor: (theme) =>
          type === "white" ? theme.palette.link.main : "#fff",
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": onClick
          ? {
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              transform: "translateY(-2px)",
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          height: "100%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              minWidth: 0,
              color: (theme) =>
                type === "white" ? "#fff" : theme.palette.gray.main,
            }}
          >
            {title}
          </Typography>
          {/* Decorative icon — not interactive (the whole card is the click
              target when `onClick` is set), kept small and pinned top-right. */}
          <Box
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: 1.5,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: (theme) => color(theme),
              bgcolor:
                type === "white"
                  ? "transparent"
                  : (theme) => color(theme) + "1A",
              "& svg": { fontSize: 20 },
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            fontWeight={600}
            title={
              denominator != null
                ? `${numberWithCommas(total)}/${numberWithCommas(denominator)}`
                : `${numberWithCommas(total)} ${unit}`
            }
            sx={{
              color: (theme) => color(theme),
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: numericFontSize(
                denominator != null
                  ? `${numberWithCommas(total)}`
                  : `${numberWithCommas(total)} ${unit}`,
                {
                  base: "1.5rem",
                  mid: "1.35rem",
                  small: "1.15rem",
                  tiny: "1rem",
                },
              ),
            }}
          >
            {numberWithCommas(total)}
            {denominator == null && ` ${unit}`}
          </Typography>
          {denominator != null && (
            <Typography
              component="span"
              fontWeight={600}
              sx={{ color: (theme) => color(theme), fontSize: "1rem", opacity: 0.6 }}
            >
              /{numberWithCommas(denominator)}
            </Typography>
          )}
          {type === "link" && onClick && (
            <KeyboardArrowRightIcon
              sx={{ color: (theme) => color(theme), ml: "auto", flexShrink: 0 }}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}
